import { connect } from 'react-redux';
import get from 'lodash.get';
import React from 'react';
import PropTypes from 'prop-types';

import CustomIcon from 'presentation/components/icons/CustomIcon';
import { CustomTextConsumer } from 'context/CustomTextContext';
import getRadius from 'lib/getRadius';
import { getSelectedSelfServiceAnswer, getSelfServiceSearchTerm, getSelfServiceSearch } from 'app/modules';
import { ICON_TYPES } from 'enums/configuration';
import overrideStyle from 'lib/overrideStyle';
import SearchContent from './SearchContent';
import SearchLoadingIndicator from './SearchLoadingIndicator';
import updateSelectedAnswer from 'actions/selfService/updateSelectedAnswer';
import getSearchResults from 'actions/selfService/getSearchResults';
import getCustomerProperties, { DEVICE_TYPE } from 'lib/getCustomerProperties';

class Search extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      focusIndex: -1,
    };

    this.clearSearch = this.clearSearch.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleMouseDown = this.handleMouseDown.bind(this);
    this.handleSetFocused = this.handleSetFocused.bind(this);
    this.onSelectAnswer = this.onSelectAnswer.bind(this);
    this.updateFocusIndex = this.updateFocusIndex.bind(this);
  }

  onSelectAnswer(selectedAnswer) {
    selectedAnswer = selectedAnswer || this.props.search.results[this.state.focusIndex];
    this.props.onSelectAnswer({
      answerId: selectedAnswer.id,
      answerName: selectedAnswer.name,
    });
  }

  clearSearch() {
    this.props.onSearch('');
  }

  componentDidMount() {
    let { device } = getCustomerProperties();
    /* Do not focus the input on Mobile devices to prevent the virtual keyboard from auto opening and potentially hiding the chat button */
    if (device !== DEVICE_TYPE.mobile) {
      this.input.focus();
    }
  }

  handleChange(ev) {
    this.props.onSearch(ev.target.value);
    this.handleSetFocused(-1);
  }

  handleFocus() {
    this.input && this.input.focus();
  }

  handleKeyDown(ev) {
    if (this.props.search.results.length > 0) {
      if (ev.key === 'ArrowUp') {
        ev.preventDefault();
        this.updateFocusIndex(-1);
      } else if (ev.key === 'ArrowDown') {
        ev.preventDefault();
        this.updateFocusIndex(1);
      } else if (ev.key === 'Enter') {
        // allows the user to press enter again and autoSelect the first answer option if it exists
        let firstAnswer = this.props.search && this.props.search.results[0];

        if (this.state.focusIndex > -1) {
          ev.preventDefault();
          this.onSelectAnswer();

          // if not null and search result exists, select the first one.
        } else if (/\S+/.test(this.props.searchTerm) && typeof firstAnswer !== 'undefined') {
          this.onSelectAnswer(firstAnswer);
          this.handleSetFocused(-1);
        }
      }
    }
  }

  handleSetFocused(focusIndex) {
    this.setState({ focusIndex });
  }

  handleMouseDown() {
    this.props.onMouseDown(this.props.answerId);
  }

  updateFocusIndex(value) {
    let max = this.props.search.results.length;
    if (max === 0) {
      return;
    }
    const { focusIndex } = this.state;
    if (value > 0 && focusIndex === this.props.search.results.length - 1) {
      return;
    } else if (value < 0 && focusIndex <= 0) {
      return;
    }
    this.handleSetFocused(focusIndex + value);
  }

  render() {
    return (
      <React.Fragment>
        <Wrapper>
          <InputWrapper>
            <SearchIconWrapper>
              <CustomIcon type={ICON_TYPES.SEARCH} />
            </SearchIconWrapper>
            <CustomTextConsumer>
              {customText => (
                <Input
                  aria-live="assertive"
                  data-aid="selfService-searchBar"
                  maxlength="30"
                  onChange={this.handleChange}
                  onFocus={this.handleFocus}
                  onKeyDown={this.handleKeyDown}
                  placeholder={customText.searchPlaceholder}
                  ref={input => (this.input = input)}
                  tabIndex="0"
                  type="search"
                  role="search"
                  value={this.props.searchTerm}
                />
              )}
            </CustomTextConsumer>
            <ClearSearch
              isVisible={!(this.props.isLoadingAnswer || this.props.search.isLoading) && this.props.searchTerm}
              onClick={this.clearSearch}
            />
            <SearchLoadingIndicator isLoading={this.props.isLoadingAnswer || this.props.search.isLoading} />
          </InputWrapper>
        </Wrapper>
        <SearchContent
          defaultContent={this.props.defaultContent}
          focusIndex={this.state.focusIndex}
          onKeyDown={this.handleKeyDown}
          onSetFocused={this.handleSetFocused}
          search={this.props.search}
        />
      </React.Fragment>
    );
  }
}

Search.propTypes = {
  defaultContent: PropTypes.node,
  onSearch: PropTypes.func.isRequired,
  onSelectAnswer: PropTypes.func.isRequired,
  search: PropTypes.shape({
    isLoading: PropTypes.bool,
    results: PropTypes.array.isRequired,
    searchTerm: PropTypes.string,
    timestamp: PropTypes.string,
  }),
};

export const mapStateToProps = state => {
  const searchTerm = getSelfServiceSearchTerm(state) || '';
  return {
    isLoadingAnswer: !!get(getSelectedSelfServiceAnswer(state), 'isLoading'),
    search: getSelfServiceSearch(state),
    searchTerm,
  };
};

export const mapDispatchToProps = dispatch => {
  return {
    onSearch: searchTerm => dispatch(getSearchResults(searchTerm)),
    onSelectAnswer: ({ answerId, answerName }) => dispatch(updateSelectedAnswer(answerId, answerName)),
  };
};

const SearchContainer = connect(
  mapStateToProps,
  mapDispatchToProps
)(Search);

export default SearchContainer;

const SearchIconWrapper = overrideStyle('div')`
  left: 20px;
  pointer-events: none;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
`;

const InputWrapper = overrideStyle('div')`
  display: flex;
  position: relative;
  width: 100%;
`;

const Input = overrideStyle('input')`
  &[type=search] {
    appearance: none;
    align-items: flex-start;
    background: #F8F8F8;
    border: 1px solid #DEDEDE;
    border-radius: ${p => getRadius(p.theme.borderRadius, 8, 50)};
    box-shadow: inset 0px 2px 2px rgba(0, 0, 0, 0.02);
    font-family:${p => p.theme.fontLibrary.secondary};
    font-size: 14px;
    line-height: normal;
    padding: 20px;
    padding-left: 48px;
    padding-right: 60px;
    transition: border-color 200ms cubic-bezier(0.645, 0.045, 0.355, 1);
    width: 100%;

    &:hover, &:focus {
      border-color: #ABABAB;
      outline: 0;
    }

    &::-ms-clear {
      display: none;
    }
    
    &::-webkit-search-cancel-button,
    &::-webkit-search-decoration,
    &::-webkit-search-results-button,
    &::-webkit-search-results-decoration { 
      display: none;
    }
  }
`;

const Wrapper = overrideStyle('div')`
  display: flex;
  padding: 0px 24px;
`;

const StyledClear = overrideStyle('div')`
  color: #979797;
  cursor: pointer;
  position: absolute;
  right: 24px;
  top: 22px;
`;

function ClearSearch({ isVisible, onClick }) {
  if (!isVisible) {
    return null;
  }
  return <StyledClear onClick={onClick}>✕</StyledClear>;
}
